/**
 * Shared SVG primitives for route map and supply flow DAG.
 * Both visualizations use the same edge (path + arrow) and optional edge-label node.
 */

import { ARROW_PATH_D, EDGE_STROKE_WIDTH, EDGE_STROKE_WIDTH_HOVER, type EdgeGeometry } from '../constants/routeGraphStyle';

const EDGE_LABEL_NODE_R = 14;
const EDGE_LABEL_NODE_FILL = '#fff';

export interface EdgeWithArrowProps {
    geometry: EdgeGeometry;
    color: string;
    strokeWidth?: number;
    strokeOpacity?: number;
    isHovered?: boolean;
    pointerEvents?: 'stroke' | 'none';
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: () => void;
}

/** Single edge: path + arrow. Same rendering as route map (multiple lines) and supply flow. */
export function EdgeWithArrow({
    geometry,
    color,
    strokeWidth = EDGE_STROKE_WIDTH,
    strokeOpacity = 0.7,
    isHovered = false,
    pointerEvents = 'stroke',
    onMouseEnter,
    onMouseLeave,
    onClick,
}: EdgeWithArrowProps) {
    const width = isHovered ? EDGE_STROKE_WIDTH_HOVER : strokeWidth;
    return (
        <g cursor={onClick ? 'pointer' : 'default'} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick}>
            <path
                d={geometry.pathD}
                fill="none"
                stroke={color}
                strokeWidth={width}
                strokeOpacity={strokeOpacity}
                strokeLinecap="round"
                strokeLinejoin="round"
                pointerEvents={pointerEvents}
            />
            <g transform={`translate(${geometry.midX},${geometry.midY}) rotate(${geometry.angleDeg + 180})`}>
                <path d={ARROW_PATH_D} fill={color} fillOpacity={0.8} stroke="none" />
            </g>
        </g>
    );
}

export interface EdgeLabelNodeProps {
    /** Center of the circle (e.g. edge midpoint) */
    cx: number;
    cy: number;
    /** Label text (e.g. flow quantity) */
    label: string | number;
    /** Stroke color for circle; supply flow uses gray so circle doesn't compete with edge colors */
    color: string;
}

/** Small circular node on an edge with label inside. Used in supply flow for flow quantity. */
export function EdgeLabelNode({ cx, cy, label, color }: EdgeLabelNodeProps) {
    return (
        <g style={{ pointerEvents: 'none' }}>
            <circle cx={cx} cy={cy} r={EDGE_LABEL_NODE_R} fill={EDGE_LABEL_NODE_FILL} stroke={color} strokeWidth={2} />
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={11} fontWeight={600} fill="#333">
                {label}
            </text>
        </g>
    );
}
